import type { L2CMetric } from '../../../types/leadToCash';

const NUMBER_FORMAT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

export function formatL2CNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, NUMBER_FORMAT);
}

export function formatL2CSignedNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const formatted = formatL2CNumber(value);
  return value > 0 ? `+${formatted}` : formatted;
}

/** Currency-prefixed amount, e.g. `formatL2CCurrency(500000, '€')` -> "€500,000". */
export function formatL2CCurrency(value: number | null | undefined, symbol = '$'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${symbol}${formatL2CNumber(value)}`;
}

/** Signed currency amount, e.g. "+€12,345" / "-€12,345". */
export function formatL2CSignedCurrency(value: number | null | undefined, symbol = '$'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const magnitude = formatL2CCurrency(Math.abs(value), symbol);
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `-${magnitude}`;
  return magnitude;
}

export function formatL2CPercent(value: number | null | undefined, signed = false): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const magnitude = Math.abs(value).toLocaleString(undefined, NUMBER_FORMAT);
  if (!signed) return `${magnitude}%`;
  if (value > 0) return `+${magnitude}%`;
  if (value < 0) return `-${magnitude}%`;
  return `${magnitude}%`;
}

export function formatMetricValue(
  metric: Pick<L2CMetric, 'value' | 'format'>,
  currencySymbol = '$',
): string {
  const v = metric.value;
  if (v == null || !Number.isFinite(v)) return '—';
  if (metric.format === 'currency') return formatL2CCurrency(v, currencySymbol);
  if (metric.format === 'percent') return `${formatL2CNumber(v)}%`;
  if (metric.format === 'ratio') return formatL2CNumber(v);
  return formatL2CNumber(v);
}

export function formatL2CValueWithSuffix(
  value: number | null | undefined,
  suffix = '',
): string {
  const formatted = formatL2CNumber(value);
  return formatted === '—' ? formatted : `${formatted}${suffix}`;
}
